

































import { Container } from 'vue-smooth-dnd'
import Group, { GroupBucket } from '@/types/Group.interface'
import BucketFormDraggable from '@/components/BucketFormDraggable.vue'
import Vue from 'vue'

interface Indexes {
    removedIndex: number
    addedIndex: number
    payload: number
}
export default Vue.extend({
    components: { BucketFormDraggable, Container },
    props: {
        bucketGroups: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            // Maps opened/closed color pickers
            pickerDisplay: {},
            colors: [
                '#6142B0',
                '#EF9A9A',
                '#FFCC80',
                '#FFF59D',
                '#A5D6A7',
                '#90CAF9',
                '#CE93D8',
                '#F48FB1',
                '#BCAAA4',
                'Custom'
            ],
            loading: false,
            buckets: null
        }
    },
    async mounted() {
        //  Set our buckets
        this.setBuckets()
    },
    computed: {
        bucketLimitReached(): boolean {
            //  If we have 0 buckets, do nothing
            if (!this.buckets) return false

            //  If we have 20 or more buckets, return true
            if (this.buckets.length >= 20) return true

            //  Else, the limit hasn't been reached, return false
            return false
        },
        defaultBucket(): GroupBucket {
            //  Create a default bucket
            return { color: '#6142B0', group: this.defaultGroup }
        },
        defaultGroup(): Group {
            //  Grab our temp bucket groups
            const groups = this.bucketGroups

            //  Find the "All Contacts" one
            const group = groups.find((x: Group) => x.name === 'All Contacts')

            //  Return the group
            return group
        },
        disableDrag(): boolean {
            //  If picker display is empty, do nothing
            if (!this.pickerDisplay) return false

            //  Iterate through our entries
            for (const [key, val] of Object.entries(this.pickerDisplay)) {
                //  If the value is true, return true
                if (val) return true
            }

            //  Else, nothing is enabled so return false
            return false
        }
    },
    methods: {
        add(): void {
            //  Grab the current length of our buckets
            const len = this.buckets.length

            //  Add a new bucket entry
            this.buckets.push({ color: '#6142B0', order: len, group: null })
        },
        childPayload(i: number): number {
            //  Return index of the child
            return i
        },
        closePickerBuffer(payload: object): void {
            //  @ts-ignore Grab the event and index
            const event = payload.event

            //  Get our HTML target
            const target = event.target as HTMLTextAreaElement

            //  If our event target has class custom-picker, ignore
            if (target.getAttribute('class') === 'custom-picker') return

            //  @ts-ignore Grab the index
            const index = payload.index

            //  Else, call our close picker
            this.closePicker(index)
        },
        closePicker(index: number): void {
            //  If the picker is opened, close it and emit the pickerClosed event
            if (this.pickerDisplay[index]) {
                this.pickerDisplay[index] = false
                return this.$emit('pickerClosed')
            }
        },
        dropped(indexes: Indexes): void {
            //  Get the new and old index of the item
            const { addedIndex, removedIndex } = indexes

            //  If the item hasn't moved, do nothing
            if (addedIndex === removedIndex) return

            //  Else, if this was the first element, do nothing
            if (addedIndex === 0 || removedIndex === 0) return

            //  From here on out, we subtract 1 from the indexes because our buckets are offset by 1

            //  Else, get the bucket at the old index
            const oldBucket = this.buckets[removedIndex - 1]

            //  Get the bucket at the new index
            const newBucket = this.buckets[addedIndex - 1]

            //  Update this bucket with its new index
            oldBucket.order = addedIndex - 1
            newBucket.order = removedIndex - 1

            //  Remove this from that index
            this.buckets.splice(removedIndex - 1, 1)

            //  Insert this bucket at the new index
            this.buckets.splice(addedIndex - 1, 0, oldBucket)
        },
        getRemovedGroups(groupIds: number[]): void {
            //  Clone our bucket groups
            const bucketGroups = window._.cloneDeep(this.bucketGroups)

            //  Find any groups that are in bucketGroups but NOT buckets
            const removal = bucketGroups.filter(
                (x: Group) => !groupIds.includes(x.id) && x.name !== 'All Contacts'
            )

            //  For each of these groups, remove the bucket from them
            removal.forEach((x: Group) => (x.bucket = null))

            //  Return the removed groups
            return removal
        },
        async onGroupSelected(selectedGroup: Group, i: number): Promise<void> {
            //  Update our buckets
            this.buckets[i].group = selectedGroup
        },
        async remove(i: number): Promise<void> {
            //  Remove this bucket
            this.buckets.splice(i, 1)
        },
        prepPayload(): Group[] {
            //  Grab our buckets
            const buckets = this.buckets

            //  Payload container
            const payload = []

            //  Container for group ids
            const groupIds = []

            //  Iterate through each bucket
            for (let i = 0; i < buckets.length; i++) {
                //  Assign group to "group" and grab our bucket without it
                let { pickerColor: _, group: group, ...bucket } = buckets[i]

                //  Make sure our bucket order is correct
                bucket.order = i

                //  Attach the bucket to our group
                group.bucket = bucket

                //  Push this into our payload
                payload.push(group)

                //  Push this id into our group ids
                groupIds.push(group.id)
            }

            //  Get our removed groups
            const removedGroups = this.getRemovedGroups(groupIds)

            //  Add these removal groups
            payload.push(...removedGroups)

            //  Return our payload
            return payload
        },
        async save(): Promise<void> {
            //  Make sure all fields have a color and a group selected
            const valid = this.validate()

            //  If not valid, do nothing
            if (!valid) return

            //  Start the loading spinner
            this.loading = true

            //  Prep our payload
            const payload = this.prepPayload()

            //  Send the request to update the groups
            await this.$store.dispatch('groupsPutBucket', { payload: payload })

            //  Stop the loading spinner
            this.loading = false

            //  Close the form and re-paginate bucket groups in AppNavigationDrawer.vue
            this.$emit('submit')
        },
        setBuckets(): void {
            //  Grab all of our groups
            const groups = this.bucketGroups

            //  Create a container for our buckets
            const buckets = []

            //  Iterate through each group
            for (let i = 0; i < groups.length; i++) {
                //  If this is the "All Conversations" group then skip
                if (groups[i].name === 'All Contacts') continue

                //  Grab the order
                let order = groups[i].bucket.order

                //  Grab our color
                let color = groups[i].bucket.color

                //  Get the picker color
                let pickerColor = this.setPickerColor(color)

                //  Create the bucket for this group
                let bucket = {
                    order: order,
                    color: color,
                    group: groups[i],
                    pickerColor: pickerColor
                }

                //  Push this bucket
                buckets.push(bucket)
            }

            //  Set our buckets
            this.buckets = buckets
        },
        setColorBuffer(color: string, idx: number): void {
            //  If the color is custom then pop our picker
            if (color === 'Custom') return this.togglePicker(idx)

            //  Else, just set our color
            this.setColor(color, idx)
        },
        setColor(color: string, idx: number): void {
            //  If object is null, do nothing
            if (!color) return

            //  Else, set the hexa
            this.buckets[idx].color = color

            //  Get our picker color
            const pickerColor = this.setPickerColor(color)

            //  Set our picker color
            this.buckets[idx].pickerColor = pickerColor
        },
        setPickerColor(color: string): string {
            //  Check if this is in our list of colors
            const exists = this.colors.findIndex((x: string) => x === color)

            //  If the index is -1, then our pickerColor is "Custom", else it is just the color
            const pickerColor = exists === -1 ? 'Custom' : color

            //  Return the picker color
            return pickerColor
        },
        //  Toggles a color picker to be on or off
        togglePicker(index: number): void {
            //  If the picker is open, close the picker
            if (this.pickerDisplay[index]) {
                return this.closePicker(index)
            }
            //  Invert the picker value
            this.pickerDisplay[index] = true

            //  This allows vue to detect the changes
            this.pickerDisplay = { ...this.pickerDisplay }
        },
        validate(): boolean {
            //  Check if there are any buckets missing groups
            const invalidGroups = this.buckets.filter((x: GroupBucket) => !x.group)

            //  If there are any invalid groups, pop a dialog
            if (invalidGroups.length) {
                this.$store.dispatch('dialogOpen', {
                    type: 'MESSAGE',
                    title: 'Group is Required',
                    text: 'Please pick a group for each view before continuing.',
                    icon: 'bi-exclamation-diamond-fill',
                    color: 'primary'
                })

                //  Return false
                return false
            }

            //  If there is a color selector with no value, valid is false
            const invalidColors = this.buckets.filter((x: GroupBucket) => !x.color)

            //  If there are any invalid colors, pop a dialog
            if (invalidColors.length) {
                this.$store.dispatch('dialogOpen', {
                    type: 'MESSAGE',
                    title: 'Color is Required',
                    text: 'Please pick a color for each view before continuing.',
                    icon: 'bi-exclamation-diamond-fill',
                    color: 'primary'
                })

                //  Return false
                return false
            }

            //  Everything passed, return true
            return true
        }
    }
})
